import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  getFirstCollision,
  MeasuringStrategy,
  pointerWithin,
  rectIntersection,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { v4 } from 'uuid';
import { FormNodeType, FormPageModel, FormSectionModel } from '../../../../shared/kiosk';
import { EditableText } from '../../../components';
import { useTranslation } from '../../../translations';
import {
  EditorNodeRenderer,
  RootFormState,
  SetPage,
  SetRoot,
  useActivePage,
  usePage,
  useSetSection,
} from './FormElements';
import { useExpressEditorConfig } from './OptionContext';
import { EditorPageRenderer } from './PageRenderer';
import { nodeEditors } from './elements';
import { DragData } from './models';
import { ExpressEditorToolbox } from './toolbox';

const TRASH_ID = 'void';

interface EditorDragContextProps {
  readonly page: FormPageModel;
  readonly setPage: SetPage;
  readonly setNextDragId: Dispatch<SetStateAction<number>>;
  readonly children: ReactNode;
}

const findContainer = (
  page: FormPageModel,
  id: UniqueIdentifier | undefined,
): FormSectionModel | undefined => {
  for (const section of page.sections) {
    if (section.id === id) {
      return section;
    }

    for (const node of section.nodes) {
      if (node.id === id) {
        return section;
      }
    }
  }

  return undefined;
};

/*
 * This component is heavily derived from the dnd-kit sample
 * https://github.com/clauderic/dnd-kit/blob/33e6dd2dc954f1f2da90d8f8af995021031b6b41/stories/2%20-%20Presets/Sortable/MultipleContainers.tsx
 */
// eslint-disable-next-line max-lines-per-function
const EditorDragContext: FC<EditorDragContextProps> = ({
  page,
  setPage,
  setNextDragId,
  children,
}) => {
  const setSection = useSetSection(setPage);
  const [activeItem, setActiveItem] = useState<(DragData & { id: string }) | null>(null);
  const [clonedItems, setClonedItems] = useState<FormPageModel | null>(null);

  const lastOverId = useRef<string | null>(null);
  const recentlyMovedToNewContainer = useRef(false);

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const data = active.data.current! as DragData;
      setActiveItem({ id: active.id as string, ...data });
      setClonedItems(page);
    },
    [page],
  );

  const isSectionId = useCallback(
    (id: UniqueIdentifier | undefined): boolean => {
      return page.sections.some((t) => t.id === id);
    },
    [page.sections],
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (isSectionId(active.id) && over?.id) {
        setPage((oldPage) => {
          const activeIndex = oldPage.sections.findIndex((t) => t.id === active.id);
          const overIndex = oldPage.sections.findIndex((t) => t.id === over.id);
          return {
            ...oldPage,
            sections: arrayMove(oldPage.sections, activeIndex, overIndex),
          };
        });
      }

      setNextDragId((o) => o + 1);

      const overId = over?.id;
      const activeContainer = findContainer(page, active.id);
      const overContainer = findContainer(page, overId);

      if (!activeContainer) {
        // Check if we are adding a new item to a container with no items
        if (overContainer) {
          setSection(overContainer.id, (s) => {
            return {
              ...s,
              nodes: s.nodes.map((n) => {
                if (n.id.startsWith('toolbox')) {
                  return { ...n, id: v4() };
                }

                return n;
              }),
            };
          });
        }

        setActiveItem(null);
        return;
      }

      if (overId === TRASH_ID) {
        /*
         * TODO - delete item?
         * setItems((items) => ({
         *   ...items,
         *   [activeContainer]: items[activeContainer].filter((id) => id !== activeId),
         * }));
         */

        setActiveItem(null);
        return;
      }

      if (overContainer) {
        const activeIndex = activeContainer.nodes.findIndex((t) => t.id === active.id);
        const overIndex = overContainer.nodes.findIndex((t) => t.id === overId);

        if (overIndex !== -1) {
          setSection(overContainer.id, (s) => {
            const nodes = arrayMove(s.nodes, activeIndex, overIndex);

            // Inserts a new item into a container with other items at a specific position
            if (nodes[overIndex]?.id.startsWith('toolbox')) {
              nodes[overIndex] = {
                ...nodes[overIndex],
                id: v4(),
              };
            }

            return { ...s, nodes };
          });
        }
      }

      setActiveItem(null);
    },
    [isSectionId, setNextDragId, page, setPage, setSection],
  );

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeItem && page.sections.some((t) => t.id === activeItem.id)) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) =>
            page.sections.some((t) => t.id === container.id),
          ),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      // If there are droppables intersecting with the pointer, return those
      const intersections =
        pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args);

      let overId = getFirstCollision(intersections, 'id');

      if (overId !== null) {
        if (overId === TRASH_ID) {
          /*
           * If the intersecting droppable is the trash, return early
           * Remove this if you're not using trashable functionality in your app
           */
          return intersections;
        }

        if (page.sections.some((t) => t.id === overId)) {
          const containerItems = page.sections.find((t) => t.id === overId)!;

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.nodes.length > 0) {
            // Return the closest droppable within that container
            const closest = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  container.id !== overId &&
                  containerItems.nodes.some((t) => t.id === container.id),
              ),
            });

            if (closest.length > 0) {
              overId = closest[0]!.id;
            }
          }
        }

        /*
         * TODO: What does this do? It currently breaks adding new items into sections
         * lastOverId.current = overId as string;
         */

        return [{ id: overId }];
      }

      /*
       * When a draggable item moves to a new container, the layout may shift
       * and the `overId` may become `null`. We manually set the cached `lastOverId`
       * to the id of the draggable item that was moved to the new container, otherwise
       * the previous `overId` will be returned which can cause items to incorrectly shift positions
       */
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeItem?.id ?? null;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [page, activeItem],
  );

  // Called when the movement of the drag item is moved between positions
  const onDragOver = useCallback(
    ({ active, over }: DragOverEvent): void => {
      const overId = over?.id;
      const activeId = `${active.id}`;

      if (overId === TRASH_ID || isSectionId(activeId)) {
        return;
      }

      const overContainer = findContainer(page, overId);
      const activeContainer = findContainer(page, activeId);

      if (!overContainer) {
        /*
         * When not hovering anything, remove the element from all other elements
         * BUG: Once an item is dragged into the screen, it can not be removed.
         * When I dynamically place the item on a droppable area, it has to have the same id as the one in the "pickup area", and it probably has some internal map from id->object.
         *
         * When dynamically place the temporary item in the droppable area, it overrides the id of the pickup area,
         * so when the dynamic item is removed, it loses track of the item in the pickup area, and causes all kinds of havok, and losing state.
         */
        /*
         *if (activeId.startsWith('toolbox') && collisions?.length === 0) {
         *  setState((oldState) => {
         *    return updateActivePage(oldState, (oldPage) => {
         *      return {
         *        ...oldPage,
         *        sections: oldPage.sections.map((s) => {
         *          return { ...s, nodes: s.nodes.filter((t) => t.id !== active.id) };
         *        }),
         *      };
         *    });
         *  });
         *}
         */
        return;
      }

      if (activeContainer?.id !== overContainer.id) {
        const overIndex = overContainer.nodes.findIndex((t) => t.id === overId);
        let newIndex: number;
        // const data = active.data.current! as DragData;
        const data = activeItem!;
        // Adding a new node
        const nodeEditor = nodeEditors[data.type];

        let newNode: FormNodeType = {
          id: activeId,
          type: data.type,
          ...nodeEditor.getDefaultValues(),
        } as FormNodeType;

        if (isSectionId(overId)) {
          newIndex = overContainer.nodes.length + 1;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top > over.rect.top + over.rect.height;

          const modifier = isBelowOverItem ? 1 : 0;

          newIndex = overIndex === -1 ? overContainer.nodes.length + 1 : overIndex + modifier;
        }

        if (recentlyMovedToNewContainer.current) {
          return;
        }

        recentlyMovedToNewContainer.current = true;

        // Remove from the source
        if (activeContainer) {
          newNode = activeContainer.nodes.find((t) => t.id === activeId)!;

          setSection(activeContainer.id, (s) => ({
            ...s,
            nodes: s.nodes.filter((t) => t.id !== activeId),
          }));
        }

        setSection(overContainer.id, (s) => ({
          ...s,
          nodes: [...s.nodes.slice(0, newIndex), newNode, ...s.nodes.slice(newIndex)],
        }));
      }
    },
    [activeItem, isSectionId, page, setSection],
  );

  const onDragCancel = useCallback(() => {
    if (clonedItems) {
      /*
       * Reset items to their original state in case items have been
       * Dragged across containers
       */
      setPage(clonedItems);
    }

    setActiveItem(null);
    setClonedItems(null);
  }, [clonedItems, setPage, setActiveItem, setClonedItems]);

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [page]);

  return (
    <DndContext
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
    >
      <div className="express-editor-main">{children}</div>

      {activeItem && activeItem.source === 'toolbox' && !isSectionId(activeItem.id) && (
        <DragOverlay
          dropAnimation={{
            duration: 500,
            easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
          }}
        >
          <EditorNodeRenderer isDragOverlay nextId={0} type={activeItem.type} />
        </DragOverlay>
      )}
    </DndContext>
  );
};

interface PageEditorContextProps {
  readonly state: RootFormState;
  readonly setState: SetRoot;
}

const PageEditorContext: FC<PageEditorContextProps> = ({ state, setState }) => {
  const config = useExpressEditorConfig();
  const [page, setPage] = useActivePage(state, setState);
  /*
   * This is a complicated situation. When Drag-and-drop elements out of the toolbox, they are temporarily created also inside of the page rendered.
   * The problem is that there is now two elements with the same id on the `DndContext`, and it loses track one of them.
   * `nextDragId` is used generate new ids to the toolbox every time a new element is placed in the form.
   */
  const [nextDragId, setNextDragId] = useState(0);

  if (!page) {
    return null;
  }

  return (
    <EditorDragContext page={page} setNextDragId={setNextDragId} setPage={setPage}>
      <main style={{ marginBottom: '55px' }}>
        <PageBody pageId={state.activePage} setState={setState} state={state} />
      </main>
      <ExpressEditorToolbox
        nextDragId={nextDragId}
        setState={setState}
        state={state}
        toolboxTypes={config.formTypes}
      />
    </EditorDragContext>
  );
};

interface FinalPageEditorContextProps {
  readonly state: RootFormState;
  readonly setState: SetRoot;
}

const FinalPageEditorContext: FC<FinalPageEditorContextProps> = ({ state, setState }) => {
  const config = useExpressEditorConfig();

  const { finalPage } = state.layout;
  const setPage: SetPage = useCallback(
    (newPage: SetStateAction<FormPageModel>) => {
      setState((oldState) => {
        return {
          ...oldState,
          layout: {
            ...oldState.layout,
            finalPage: {
              ...(typeof newPage === 'function' ? newPage(oldState.layout.finalPage) : newPage),
              id: 'final',
            },
          },
        };
      });
    },
    [setState],
  );

  /*
   * This is a complicated situation. When Drag-and-drop elements out of the toolbox, they are temporarily created also inside of the page rendered.
   * The problem is that there is now two elements with the same id on the `DndContext`, and it loses track one of them.
   * `nextDragId` is used generate new ids to the toolbox every time a new element is placed in the form.
   */
  const [nextDragId, setNextDragId] = useState(0);

  if (!config.finalPage) {
    return null;
  }

  return (
    <EditorDragContext page={finalPage} setNextDragId={setNextDragId} setPage={setPage}>
      <main style={{ marginBottom: '55px' }}>
        <EditorPageRenderer page={finalPage} setPage={setPage} />
      </main>
      <ExpressEditorToolbox
        nextDragId={nextDragId}
        setState={setState}
        state={state}
        toolboxTypes={config.finalPage.formTypes}
      />
    </EditorDragContext>
  );
};

interface PageEditorProps {
  readonly state: RootFormState;
  readonly setState: SetRoot;
}

export const PageEditor: FC<PageEditorProps> = ({ state, setState }) => {
  if (state.activePage === 'final') {
    return <FinalPageEditorContext setState={setState} state={state} />;
  }

  return <PageEditorContext setState={setState} state={state} />;
};

interface PageBodyProps {
  readonly state: RootFormState;
  readonly setState: SetRoot;
  readonly pageId: string | null;
}

const PageBody: FC<PageBodyProps> = ({ state, setState, pageId }) => {
  const [page, setPage] = usePage(state, pageId!, setState);
  const { ts } = useTranslation();

  if (!page) {
    return ts('no_page');
  }

  return (
    <>
      <EditableText
        onChange={(name) => {
          setPage({ ...page, name });
        }}
        text={page.name}
      />
      <EditorPageRenderer page={page} setPage={setPage} />
    </>
  );
};
